import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import {
  Building2, ChevronRight, ChevronDown, Check, ExternalLink, AlertCircle,
  Users, Shield, FileText, DollarSign, ArrowRight, CircleDot,
  CheckCircle2, Circle, BookOpen, X, MapPin
} from 'lucide-react';

const BUSINESS_TYPES = [
  {
    id: 'llc',
    name: 'LLC',
    full_name: 'Limited Liability Company',
    best_for: 'Most small businesses, freelancers, and side hustles',
    popularity: 'Most Popular',
    icon: Shield,
    color: 'indigo',
    summary: 'An LLC protects your personal assets (home, car, savings) if your business gets sued or has debts. It\'s the easiest and most flexible option for most people.',
    pros: [
      'Your personal assets are protected from business debts',
      'Simple to set up and maintain — minimal paperwork',
      'Flexible taxes — choose how you want to be taxed',
      'No limit on number of owners (called "members")',
      'Less formal than a corporation — no board meetings required',
    ],
    cons: [
      'Small annual state fee (varies by state, usually $50-$300/yr)',
      'Some states charge franchise taxes on LLCs',
      'Harder to raise money from investors compared to a Corporation',
    ],
    cost_range: '$50 - $500 to form (varies by state)',
    time: '1-3 weeks typically',
    who_should: 'Restaurants, salons, consultants, online stores, churches with commercial activity, freelancers, rental property owners',
    plain_english: 'Think of an LLC like a shield between you and your business. If something goes wrong with the business, your personal stuff is protected. It\'s the #1 choice for small businesses because it\'s simple, affordable, and gives you real protection.',
  },
  {
    id: 'sole_prop',
    name: 'Sole Proprietorship / DBA',
    full_name: 'Sole Proprietorship (with optional DBA)',
    best_for: 'Very small operations, testing a business idea',
    popularity: 'Simplest Option',
    icon: CircleDot,
    color: 'emerald',
    summary: 'This is the simplest business structure — you and the business are the same thing legally. A DBA ("Doing Business As") lets you use a business name instead of your personal name.',
    pros: [
      'No paperwork to start — you\'re automatically a sole proprietor',
      'No formation fees (DBA is usually $10-$50)',
      'All profits go directly to you',
      'Simplest tax filing — report on your personal return',
      'Full control — no partners or board to answer to',
    ],
    cons: [
      'NO personal asset protection — your home, car, and savings are at risk',
      'Harder to get business loans or credit',
      'Looks less professional to some customers',
      'Can\'t bring on partners easily',
      'If you get sued, they can come after everything you own',
    ],
    cost_range: 'Free to start (DBA filing: $10-$50)',
    time: 'Immediate (DBA: 1-5 days)',
    who_should: 'Freelance writers, tutors, lawn care, babysitters, testing a business idea before committing',
    plain_english: 'A sole proprietorship means there\'s no legal separation between you and your business. It\'s free and easy, but the big downside is that if your business owes money or gets sued, your personal stuff is on the line. Most people upgrade to an LLC once they start making real money.',
  },
  {
    id: 'corporation',
    name: 'Corporation',
    full_name: 'Corporation (C-Corp or S-Corp)',
    best_for: 'Businesses planning to raise investment or go big',
    popularity: 'For Growth',
    icon: Building2,
    color: 'cyan',
    summary: 'A corporation is a more formal business structure. It\'s a separate legal entity that can raise money by selling shares. Best for businesses planning to grow significantly or attract investors.',
    pros: [
      'Strongest personal asset protection',
      'Can sell stock to raise money from investors',
      'Easier to transfer ownership',
      'Established credibility with banks and partners',
      'S-Corp election can save on self-employment taxes',
    ],
    cons: [
      'More expensive to set up and maintain',
      'Requires formal structure — board of directors, meetings, minutes',
      'C-Corps face "double taxation" (corporate tax + personal tax on dividends)',
      'More complex tax filings',
      'Strict record-keeping requirements',
    ],
    cost_range: '$100 - $800 to form (varies by state)',
    time: '1-4 weeks',
    who_should: 'Tech startups seeking investors, businesses planning to go public, companies with multiple shareholders',
    plain_english: 'A corporation is like creating a brand new "person" in the eyes of the law. It\'s more complex than an LLC but necessary if you want to raise money from investors or eventually sell stock. Most small businesses don\'t need this — an LLC is usually better.',
    subtypes: [
      { name: 'C-Corp', desc: 'Standard corporation. Pays its own taxes. Can have unlimited shareholders. Required if you want venture capital.' },
      { name: 'S-Corp', desc: 'Special tax election — profits pass through to your personal return (avoids double taxation). Limited to 100 shareholders, all must be US citizens.' },
    ],
  },
  {
    id: 'nonprofit',
    name: 'Non-Profit',
    full_name: 'Non-Profit Organization (501(c)(3))',
    best_for: 'Churches, charities, community organizations',
    popularity: 'For a Cause',
    icon: Users,
    color: 'purple',
    summary: 'A non-profit is an organization that operates for a charitable, religious, educational, or community purpose. Donations to your organization can be tax-deductible for donors.',
    pros: [
      'Tax-exempt — don\'t pay federal income tax on revenue',
      'Donors can deduct contributions on their taxes',
      'Eligible for grants and special funding',
      'Limited liability protection for directors and officers',
      'Public trust and credibility',
    ],
    cons: [
      'Cannot distribute profits to owners or members',
      'Must serve a public purpose (not personal enrichment)',
      'Extensive paperwork — IRS Form 1023 or 1023-EZ required',
      'Subject to public scrutiny — financial records are public',
      'Must maintain non-profit purpose to keep tax-exempt status',
    ],
    cost_range: '$275 - $600 IRS filing fee + state fees',
    time: '3-6 months (IRS approval can take time)',
    who_should: 'Churches, ministries, charities, community foundations, educational organizations, food banks',
    plain_english: 'A non-profit lets you operate for a cause instead of profit. The big benefits are tax exemption and the ability to receive tax-deductible donations. Churches are automatically considered 501(c)(3) but many still file for official recognition. It takes more time to set up, but grants and donations make it worthwhile.',
  },
];

const STATE_DATA = {
  AL: { name: 'Alabama', sos_url: 'https://www.sos.alabama.gov/business-services', filing_fee: '$200', annual_fee: '$0 (but franchise tax applies)' },
  AK: { name: 'Alaska', sos_url: 'https://www.commerce.alaska.gov/web/cbpl/corporations', filing_fee: '$250', annual_fee: '$100 biennial report' },
  AZ: { name: 'Arizona', sos_url: 'https://azsos.gov/business', filing_fee: '$50', annual_fee: '$0' },
  AR: { name: 'Arkansas', sos_url: 'https://www.sos.arkansas.gov/business-commercial-services-bcs', filing_fee: '$45', annual_fee: '$150 franchise tax' },
  CA: { name: 'California', sos_url: 'https://www.sos.ca.gov/business-programs', filing_fee: '$70', annual_fee: '$800 franchise tax' },
  CO: { name: 'Colorado', sos_url: 'https://www.sos.state.co.us/biz/', filing_fee: '$50', annual_fee: '$10 periodic report' },
  CT: { name: 'Connecticut', sos_url: 'https://business.ct.gov/', filing_fee: '$120', annual_fee: '$80 annual report' },
  DE: { name: 'Delaware', sos_url: 'https://corp.delaware.gov/', filing_fee: '$90', annual_fee: '$300 franchise tax' },
  FL: { name: 'Florida', sos_url: 'https://dos.fl.gov/sunbiz/', filing_fee: '$125', annual_fee: '$138.75 annual report' },
  GA: { name: 'Georgia', sos_url: 'https://sos.ga.gov/corporations-division', filing_fee: '$100', annual_fee: '$50 annual registration' },
  HI: { name: 'Hawaii', sos_url: 'https://cca.hawaii.gov/breg/', filing_fee: '$50', annual_fee: '$15 annual report' },
  ID: { name: 'Idaho', sos_url: 'https://sos.idaho.gov/business/', filing_fee: '$100', annual_fee: '$0' },
  IL: { name: 'Illinois', sos_url: 'https://www.ilsos.gov/departments/business_services/', filing_fee: '$150', annual_fee: '$75 annual report' },
  IN: { name: 'Indiana', sos_url: 'https://www.in.gov/sos/business/', filing_fee: '$95', annual_fee: '$31 biennial report' },
  IA: { name: 'Iowa', sos_url: 'https://sos.iowa.gov/business/', filing_fee: '$50', annual_fee: '$45 biennial report' },
  KS: { name: 'Kansas', sos_url: 'https://www.sos.ks.gov/business/', filing_fee: '$160', annual_fee: '$55 annual report' },
  KY: { name: 'Kentucky', sos_url: 'https://www.sos.ky.gov/bus/', filing_fee: '$40', annual_fee: '$15 annual report' },
  LA: { name: 'Louisiana', sos_url: 'https://www.sos.la.gov/BusinessServices/', filing_fee: '$100', annual_fee: '$35 annual report' },
  ME: { name: 'Maine', sos_url: 'https://www.maine.gov/sos/cec/corp/', filing_fee: '$175', annual_fee: '$85 annual report' },
  MD: { name: 'Maryland', sos_url: 'https://egov.maryland.gov/BusinessExpress', filing_fee: '$100', annual_fee: '$300 annual report + personal property return' },
  MA: { name: 'Massachusetts', sos_url: 'https://www.sec.state.ma.us/cor/', filing_fee: '$500', annual_fee: '$500 annual report' },
  MI: { name: 'Michigan', sos_url: 'https://www.michigan.gov/lara/bureau-list/bcs', filing_fee: '$50', annual_fee: '$25 annual statement' },
  MN: { name: 'Minnesota', sos_url: 'https://www.sos.state.mn.us/business-liens/', filing_fee: '$155', annual_fee: '$0 annual renewal' },
  MS: { name: 'Mississippi', sos_url: 'https://www.sos.ms.gov/business-services', filing_fee: '$50', annual_fee: '$0' },
  MO: { name: 'Missouri', sos_url: 'https://www.sos.mo.gov/business', filing_fee: '$50', annual_fee: '$0' },
  MT: { name: 'Montana', sos_url: 'https://sosmt.gov/business/', filing_fee: '$70', annual_fee: '$20 annual report' },
  NE: { name: 'Nebraska', sos_url: 'https://sos.nebraska.gov/business-services', filing_fee: '$100', annual_fee: '$13 biennial report' },
  NV: { name: 'Nevada', sos_url: 'https://www.nvsos.gov/sos/businesses', filing_fee: '$75', annual_fee: '$150 annual list + $200 business license' },
  NH: { name: 'New Hampshire', sos_url: 'https://www.sos.nh.gov/corporation-division', filing_fee: '$100', annual_fee: '$100 annual report' },
  NJ: { name: 'New Jersey', sos_url: 'https://www.njportal.com/DOR/BusinessFormation/', filing_fee: '$125', annual_fee: '$75 annual report' },
  NM: { name: 'New Mexico', sos_url: 'https://www.sos.state.nm.us/business-services/', filing_fee: '$50', annual_fee: '$0' },
  NY: { name: 'New York', sos_url: 'https://dos.ny.gov/business-filings', filing_fee: '$200', annual_fee: '$9 biennial statement + publication requirement' },
  NC: { name: 'North Carolina', sos_url: 'https://www.sosnc.gov/divisions/business_registration', filing_fee: '$125', annual_fee: '$200 annual report' },
  ND: { name: 'North Dakota', sos_url: 'https://sos.nd.gov/business/', filing_fee: '$135', annual_fee: '$50 annual report' },
  OH: { name: 'Ohio', sos_url: 'https://www.ohiosos.gov/businesses/', filing_fee: '$99', annual_fee: '$0' },
  OK: { name: 'Oklahoma', sos_url: 'https://www.sos.ok.gov/business/', filing_fee: '$100', annual_fee: '$25 annual certificate' },
  OR: { name: 'Oregon', sos_url: 'https://sos.oregon.gov/business/', filing_fee: '$100', annual_fee: '$100 annual report' },
  PA: { name: 'Pennsylvania', sos_url: 'https://www.dos.pa.gov/BusinessCharities/', filing_fee: '$125', annual_fee: '$7 annual report (decennial)' },
  RI: { name: 'Rhode Island', sos_url: 'https://business.sos.ri.gov/', filing_fee: '$150', annual_fee: '$50 annual report' },
  SC: { name: 'South Carolina', sos_url: 'https://sos.sc.gov/online-filings', filing_fee: '$110', annual_fee: '$0' },
  SD: { name: 'South Dakota', sos_url: 'https://sdsos.gov/business-services/', filing_fee: '$150', annual_fee: '$50 annual report' },
  TN: { name: 'Tennessee', sos_url: 'https://sos.tn.gov/business-services', filing_fee: '$300', annual_fee: '$300 annual report' },
  TX: { name: 'Texas', sos_url: 'https://www.sos.texas.gov/corp/', filing_fee: '$300', annual_fee: '$0 (franchise tax may apply)' },
  UT: { name: 'Utah', sos_url: 'https://corporations.utah.gov/', filing_fee: '$54', annual_fee: '$18 annual renewal' },
  VT: { name: 'Vermont', sos_url: 'https://sos.vermont.gov/corporations/', filing_fee: '$125', annual_fee: '$35 annual report' },
  VA: { name: 'Virginia', sos_url: 'https://www.scc.virginia.gov/pages/Business-Entity-Search', filing_fee: '$100', annual_fee: '$50 annual registration fee' },
  WA: { name: 'Washington', sos_url: 'https://www.sos.wa.gov/corporations-charities/', filing_fee: '$200', annual_fee: '$60 annual report' },
  WV: { name: 'West Virginia', sos_url: 'https://sos.wv.gov/business/', filing_fee: '$100', annual_fee: '$25 annual report' },
  WI: { name: 'Wisconsin', sos_url: 'https://www.wdfi.org/corporations/', filing_fee: '$130', annual_fee: '$25 annual report' },
  WY: { name: 'Wyoming', sos_url: 'https://sos.wyo.gov/Business/', filing_fee: '$100', annual_fee: '$60 annual report' },
  DC: { name: 'Washington D.C.', sos_url: 'https://corponline.dcra.dc.gov/', filing_fee: '$220', annual_fee: '$300 biennial report' },
};

const EIN_STEPS = [
  {
    title: 'What is an EIN and do you need one?',
    content: 'An EIN (Employer Identification Number) is like a Social Security number for your business. The IRS uses it to identify your business for tax purposes.',
    details: [
      'You need an EIN if you have employees, operate as a corporation or partnership, or file certain tax returns',
      'Even if not required, getting an EIN is recommended — it keeps your personal SSN off business documents',
      'An EIN is completely free from the IRS — never pay a third party for this',
      'You can only apply for one EIN per day',
    ],
  },
  {
    title: 'Gather your information',
    content: 'Before starting, have these ready:',
    details: [
      'Your Social Security Number (SSN) or existing EIN of the responsible party',
      'Your legal business name (must match your state formation documents)',
      'Your business address (can be a home address)',
      'The type of business entity you formed (LLC, Corporation, etc.)',
      'The date you formed or acquired the business',
      'Your principal business activity (what your business does)',
    ],
  },
  {
    title: 'Apply online at IRS.gov',
    content: 'The IRS online application is the fastest way — you get your EIN immediately.',
    details: [
      'Available Monday-Friday, 7am-10pm Eastern Time',
      'The application takes about 10-15 minutes',
      'You\'ll answer questions about your business type, reason for applying, and responsible party',
      'At the end, you\'ll receive your EIN immediately on screen',
      'Print or save the confirmation — the IRS will also mail a confirmation letter (CP 575)',
    ],
    action_url: 'https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online',
    action_label: 'Go to IRS EIN Application',
  },
  {
    title: 'After you get your EIN',
    content: 'Once you have your EIN, here\'s what to do with it:',
    details: [
      'Open a business bank account (most banks require an EIN)',
      'Use it on tax returns, business licenses, and permits',
      'Give it to clients instead of your SSN when they need a W-9',
      'Keep the confirmation letter (CP 575) in a safe place — it\'s hard to get a replacement',
      'Update your business records with the new EIN',
    ],
  },
];

const FORMATION_CHECKLIST = [
  { id: 'choose_type', label: 'Choose your business type', section: 'type' },
  { id: 'choose_state', label: 'Choose your state', section: 'state' },
  { id: 'check_name', label: 'Check if your business name is available', section: 'state' },
  { id: 'file_state', label: 'File formation documents with your state', section: 'state' },
  { id: 'get_ein', label: 'Get your EIN from the IRS', section: 'ein' },
  { id: 'open_bank', label: 'Open a business bank account', section: 'after' },
  { id: 'get_licenses', label: 'Check for local business licenses/permits', section: 'after' },
  { id: 'get_website', label: 'Create your business website', section: 'after' },
];

export default function BusinessFormation() {
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedType, setSelectedType] = useState(null);
  const [expandedType, setExpandedType] = useState(null);
  const [selectedState, setSelectedState] = useState('');
  const [checklist, setChecklist] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProgress = async () => {
      try {
        const res = await api.getFormationProgress();
        const data = res.data;
        if (data.selected_type) setSelectedType(data.selected_type);
        if (data.selected_state) setSelectedState(data.selected_state);
        if (data.checklist) setChecklist(data.checklist);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    loadProgress();
  }, []);

  const saveProgress = async (updates) => {
    const newState = {
      selected_type: updates.selected_type ?? selectedType,
      selected_state: updates.selected_state ?? selectedState,
      checklist: updates.checklist ?? checklist,
    };
    try { await api.saveFormationProgress(newState); } catch (err) { console.error(err); }
  };

  const toggleCheck = (id) => {
    const updated = { ...checklist, [id]: !checklist[id] };
    setChecklist(updated);
    saveProgress({ checklist: updated });
  };

  const selectType = (typeId) => {
    setSelectedType(typeId);
    saveProgress({ selected_type: typeId });
  };

  const selectState = (stateCode) => {
    setSelectedState(stateCode);
    saveProgress({ selected_state: stateCode });
  };

  const completedCount = FORMATION_CHECKLIST.filter(c => checklist[c.id]).length;
  const progress = Math.round((completedCount / FORMATION_CHECKLIST.length) * 100);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'type', label: '1. Business Type' },
    { id: 'state', label: '2. State Registration' },
    { id: 'ein', label: '3. Get Your EIN' },
    { id: 'checklist', label: 'Checklist' },
  ];

  return (
    <div className="animate-fade-in" data-testid="business-formation-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Business Formation</h2>
          <p className="text-sm text-slate-400 mt-1">Step-by-step guide to legally forming your business</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right mr-3">
            <p className="text-xs text-slate-500">Progress</p>
            <p className="text-sm font-semibold text-indigo-400">{completedCount}/{FORMATION_CHECKLIST.length} steps</p>
          </div>
          <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* Section Nav */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)} className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`} data-testid={`formation-section-${s.id}`}>
            {s.label}
          </button>
        ))}
      </div>

      {activeSection === 'overview' && <OverviewSection progress={progress} completedCount={completedCount} selectedType={selectedType} selectedState={selectedState} onNavigate={setActiveSection} />}
      {activeSection === 'type' && <TypeSection selectedType={selectedType} expandedType={expandedType} setExpandedType={setExpandedType} onSelect={selectType} onCheck={toggleCheck} checklist={checklist} />}
      {activeSection === 'state' && <StateSection selectedState={selectedState} onSelect={selectState} selectedType={selectedType} onCheck={toggleCheck} checklist={checklist} />}
      {activeSection === 'ein' && <EINSection onCheck={toggleCheck} checklist={checklist} />}
      {activeSection === 'checklist' && <ChecklistSection checklist={checklist} onToggle={toggleCheck} selectedType={selectedType} selectedState={selectedState} />}
    </div>
  );
}

function OverviewSection({ progress, completedCount, selectedType, selectedState, onNavigate }) {
  const type = BUSINESS_TYPES.find(t => t.id === selectedType);
  const state = STATE_DATA[selectedState];

  return (
    <div className="space-y-6">
      {/* Welcome card */}
      <div className="rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-indigo-500/10 to-slate-900/60 p-6">
        <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Starting a business? We'll walk you through it.</h3>
        <p className="text-sm text-slate-400 mb-4">Forming a business is easier than you think. Follow these three steps and you'll be official. Everything here is free — we just show you what to do and where to go.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button onClick={() => onNavigate('type')} className="text-left rounded-xl bg-slate-800/40 border border-slate-700 hover:border-indigo-500/30 p-4 transition-all group" data-testid="nav-to-type">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-xs font-bold">1</div>
              <p className="text-sm font-medium text-white group-hover:text-indigo-300 transition-colors">Choose Your Type</p>
            </div>
            <p className="text-xs text-slate-500">LLC, Corporation, Non-Profit, or Sole Proprietorship</p>
            {type && <p className="text-xs text-indigo-400 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {type.name}</p>}
          </button>
          <button onClick={() => onNavigate('state')} className="text-left rounded-xl bg-slate-800/40 border border-slate-700 hover:border-indigo-500/30 p-4 transition-all group" data-testid="nav-to-state">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-xs font-bold">2</div>
              <p className="text-sm font-medium text-white group-hover:text-indigo-300 transition-colors">Register in Your State</p>
            </div>
            <p className="text-xs text-slate-500">File with your Secretary of State</p>
            {state && <p className="text-xs text-indigo-400 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {state.name}</p>}
          </button>
          <button onClick={() => onNavigate('ein')} className="text-left rounded-xl bg-slate-800/40 border border-slate-700 hover:border-indigo-500/30 p-4 transition-all group" data-testid="nav-to-ein">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-xs font-bold">3</div>
              <p className="text-sm font-medium text-white group-hover:text-indigo-300 transition-colors">Get Your EIN</p>
            </div>
            <p className="text-xs text-slate-500">Free from the IRS — takes 15 minutes</p>
          </button>
        </div>
      </div>

      {/* Quick tips */}
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-300 mb-2">Important things to know</p>
            <ul className="space-y-1.5 text-xs text-slate-400">
              <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">-</span> Never pay a service hundreds of dollars to get your EIN — it's free directly from the IRS</li>
              <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">-</span> Most small businesses should start as an LLC — it's the best balance of simplicity and protection</li>
              <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">-</span> You can form your business in any state, but your home state is usually cheapest and simplest</li>
              <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">-</span> Keep all your formation documents in a safe place — you'll need them for bank accounts and licenses</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function TypeSection({ selectedType, expandedType, setExpandedType, onSelect, onCheck, checklist }) {
  return (
    <div className="space-y-4" data-testid="type-section">
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 mb-4">
        <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Outfit' }}>Which business type is right for you?</h3>
        <p className="text-sm text-slate-400">Click on each option to learn more. We explain everything in plain English — no legal jargon.</p>
      </div>

      {BUSINESS_TYPES.map(bt => {
        const isSelected = selectedType === bt.id;
        const isExpanded = expandedType === bt.id;
        const Icon = bt.icon;
        const borderColor = isSelected ? `border-${bt.color}-500/40` : 'border-slate-800';
        const bgColor = isSelected ? `bg-${bt.color}-500/5` : 'bg-slate-900/40';

        return (
          <div key={bt.id} className={`rounded-xl border ${isSelected ? 'border-indigo-500/40 bg-indigo-500/5' : 'border-slate-800 bg-slate-900/40'} overflow-hidden transition-all`} data-testid={`business-type-${bt.id}`}>
            {/* Header */}
            <button onClick={() => setExpandedType(isExpanded ? null : bt.id)} className="w-full flex items-center justify-between p-5 text-left">
              <div className="flex items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isSelected ? 'bg-indigo-500/20' : 'bg-slate-800'}`}>
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-indigo-400' : 'text-slate-400'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-base font-semibold text-white">{bt.name}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded ${bt.id === 'llc' ? 'bg-indigo-500/20 text-indigo-400' : bt.id === 'sole_prop' ? 'bg-emerald-500/20 text-emerald-400' : bt.id === 'corporation' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-purple-500/20 text-purple-400'}`}>{bt.popularity}</span>
                    {isSelected && <CheckCircle2 className="w-4 h-4 text-indigo-400" />}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{bt.best_for}</p>
                </div>
              </div>
              {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </button>

            {/* Expanded detail */}
            {isExpanded && (
              <div className="px-5 pb-5 space-y-4 border-t border-slate-800/50 pt-4">
                {/* Plain English explanation */}
                <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-4 h-4 text-indigo-400" />
                    <p className="text-sm font-medium text-indigo-300">In plain English</p>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">{bt.plain_english}</p>
                </div>

                {/* Pros & Cons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-4">
                    <p className="text-xs font-medium text-emerald-400 mb-2">Advantages</p>
                    <ul className="space-y-1.5">
                      {bt.pros.map((p, i) => <li key={i} className="flex items-start gap-2 text-xs text-slate-300"><Check className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5" />{p}</li>)}
                    </ul>
                  </div>
                  <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-4">
                    <p className="text-xs font-medium text-red-400 mb-2">Disadvantages</p>
                    <ul className="space-y-1.5">
                      {bt.cons.map((c, i) => <li key={i} className="flex items-start gap-2 text-xs text-slate-300"><X className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />{c}</li>)}
                    </ul>
                  </div>
                </div>

                {/* Key facts */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-3 text-center">
                    <DollarSign className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <p className="text-[10px] text-slate-500">Typical Cost</p>
                    <p className="text-xs font-medium text-white">{bt.cost_range}</p>
                  </div>
                  <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-3 text-center">
                    <FileText className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <p className="text-[10px] text-slate-500">Time to Form</p>
                    <p className="text-xs font-medium text-white">{bt.time}</p>
                  </div>
                  <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-3 text-center">
                    <Users className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <p className="text-[10px] text-slate-500">Best For</p>
                    <p className="text-xs font-medium text-white">{bt.who_should.split(',')[0]}</p>
                  </div>
                </div>

                {/* Corp subtypes */}
                {bt.subtypes && (
                  <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-4">
                    <p className="text-xs font-medium text-cyan-400 mb-2">Corporation Types</p>
                    <div className="space-y-2">
                      {bt.subtypes.map((st, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-xs font-semibold text-white min-w-[50px]">{st.name}:</span>
                          <span className="text-xs text-slate-400">{st.desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Select button */}
                <button onClick={() => { onSelect(bt.id); onCheck('choose_type'); }} className={`w-full h-11 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${isSelected ? 'bg-indigo-500/20 border border-indigo-500/30 text-indigo-300' : 'bg-indigo-500 hover:bg-indigo-600 text-white'}`} data-testid={`select-type-${bt.id}`}>
                  {isSelected ? <><CheckCircle2 className="w-4 h-4" /> This is my business type</> : <><ArrowRight className="w-4 h-4" /> Choose {bt.name}</>}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StateSection({ selectedState, onSelect, selectedType, onCheck, checklist }) {
  const stateEntries = Object.entries(STATE_DATA).sort((a, b) => a[1].name.localeCompare(b[1].name));
  const state = STATE_DATA[selectedState];
  const type = BUSINESS_TYPES.find(t => t.id === selectedType);

  return (
    <div className="space-y-4" data-testid="state-section">
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
        <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Outfit' }}>Register in your state</h3>
        <p className="text-sm text-slate-400">Most businesses should register in the state where they operate. Select your state to get specific instructions, costs, and a direct link to file.</p>
      </div>

      {/* State selector */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
        <label className="block text-sm text-slate-300 mb-2">Where will your business operate?</label>
        <select value={selectedState} onChange={e => { onSelect(e.target.value); if (e.target.value) onCheck('choose_state'); }} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 outline-none" data-testid="state-select">
          <option value="">Select your state...</option>
          {stateEntries.map(([code, s]) => <option key={code} value={code}>{s.name}</option>)}
        </select>
      </div>

      {/* State detail */}
      {state && (
        <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-6 space-y-4">
          <div className="flex items-center gap-3 mb-1">
            <MapPin className="w-5 h-5 text-indigo-400" />
            <h4 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{state.name}</h4>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-4">
              <p className="text-xs text-slate-500 mb-1">Formation Filing Fee</p>
              <p className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{state.filing_fee}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{type ? `For an ${type.name}` : 'Typical LLC fee'}</p>
            </div>
            <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-4">
              <p className="text-xs text-slate-500 mb-1">Annual / Ongoing Fee</p>
              <p className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{state.annual_fee}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Required to stay in good standing</p>
            </div>
          </div>

          {/* Steps */}
          <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-4">
            <p className="text-sm font-medium text-white mb-3">How to register in {state.name}:</p>
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-xs font-bold text-indigo-400">1</span></div>
                <div>
                  <p className="text-sm text-slate-200 font-medium">Choose a business name</p>
                  <p className="text-xs text-slate-400">Search your state's business registry to make sure the name isn't taken. Most states have a free search tool on their website.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-xs font-bold text-indigo-400">2</span></div>
                <div>
                  <p className="text-sm text-slate-200 font-medium">Choose a registered agent</p>
                  <p className="text-xs text-slate-400">A registered agent receives legal documents on behalf of your business. You can be your own registered agent in most states, or hire a service ($50-$150/yr).</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-xs font-bold text-indigo-400">3</span></div>
                <div>
                  <p className="text-sm text-slate-200 font-medium">File your formation documents</p>
                  <p className="text-xs text-slate-400">
                    {selectedType === 'llc' ? 'File "Articles of Organization" with the Secretary of State.' :
                     selectedType === 'corporation' ? 'File "Articles of Incorporation" with the Secretary of State.' :
                     selectedType === 'nonprofit' ? 'File "Articles of Incorporation" (non-profit) with the Secretary of State.' :
                     selectedType === 'sole_prop' ? 'File a DBA ("Doing Business As") with your county or state.' :
                     'File the appropriate formation documents with the Secretary of State.'}
                    {' '}Most states let you file online.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-xs font-bold text-indigo-400">4</span></div>
                <div>
                  <p className="text-sm text-slate-200 font-medium">Create an operating agreement</p>
                  <p className="text-xs text-slate-400">Even if your state doesn't require one, an operating agreement outlines how your business will be run. Free templates are available online.</p>
                </div>
              </li>
            </ol>
          </div>

          <a href={state.sos_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-indigo-500 hover:bg-indigo-600 text-white h-12 rounded-lg transition-all font-medium text-sm" data-testid="state-sos-link">
            <ExternalLink className="w-4 h-4" /> Go to {state.name} Secretary of State
          </a>
          <p className="text-[10px] text-slate-600 text-center">Opens the official state website where you can search names and file your business documents</p>
        </div>
      )}
    </div>
  );
}

function EINSection({ onCheck, checklist }) {
  const [expandedStep, setExpandedStep] = useState(0);

  return (
    <div className="space-y-4" data-testid="ein-section">
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
        <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Outfit' }}>Get your EIN (Employer Identification Number)</h3>
        <p className="text-sm text-slate-400">An EIN is like a Social Security number for your business. It's completely free from the IRS and takes about 15 minutes to get online.</p>
      </div>

      {/* Warning about scams */}
      <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-300">Watch out for EIN scams</p>
            <p className="text-xs text-slate-400 mt-1">Many websites charge $50-$300 to "get your EIN." The IRS provides this completely free. Only use <strong className="text-white">IRS.gov</strong> — the official link is provided below.</p>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {EIN_STEPS.map((step, i) => {
          const isExpanded = expandedStep === i;
          return (
            <div key={i} className={`rounded-xl border ${isExpanded ? 'border-indigo-500/30 bg-indigo-500/5' : 'border-slate-800 bg-slate-900/40'} overflow-hidden transition-all`}>
              <button onClick={() => setExpandedStep(isExpanded ? -1 : i)} className="w-full flex items-center justify-between p-4 text-left">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isExpanded ? 'bg-indigo-500/20' : 'bg-slate-800'}`}>
                    <span className={`text-sm font-bold ${isExpanded ? 'text-indigo-400' : 'text-slate-400'}`}>{i + 1}</span>
                  </div>
                  <p className="text-sm font-medium text-white">{step.title}</p>
                </div>
                {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 space-y-3">
                  <p className="text-sm text-slate-300 ml-11">{step.content}</p>
                  <ul className="space-y-1.5 ml-11">
                    {step.details.map((d, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs text-slate-400">
                        <Check className="w-3 h-3 text-indigo-400 flex-shrink-0 mt-0.5" />{d}
                      </li>
                    ))}
                  </ul>
                  {step.action_url && (
                    <div className="ml-11 pt-2">
                      <a href={step.action_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-all" data-testid="irs-ein-link" onClick={() => onCheck('get_ein')}>
                        <ExternalLink className="w-4 h-4" /> {step.action_label}
                      </a>
                      <p className="text-[10px] text-slate-600 mt-2">Opens the official IRS.gov website — completely free</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChecklistSection({ checklist, onToggle, selectedType, selectedState }) {
  const type = BUSINESS_TYPES.find(t => t.id === selectedType);
  const state = STATE_DATA[selectedState];

  return (
    <div className="space-y-4" data-testid="checklist-section">
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
        <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Outfit' }}>Your Formation Checklist</h3>
        <p className="text-sm text-slate-400">Track your progress. Check off each step as you complete it.</p>
      </div>

      {/* Summary */}
      {(type || state) && (
        <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4">
          <p className="text-xs text-slate-500 mb-2">Your selections:</p>
          <div className="flex gap-3">
            {type && <span className="text-xs bg-indigo-500/20 text-indigo-300 px-3 py-1 rounded-lg">{type.name}</span>}
            {state && <span className="text-xs bg-indigo-500/20 text-indigo-300 px-3 py-1 rounded-lg">{state.name}</span>}
          </div>
        </div>
      )}

      {/* Checklist items */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        {FORMATION_CHECKLIST.map((item, i) => {
          const isChecked = checklist[item.id];
          return (
            <button key={item.id} onClick={() => onToggle(item.id)} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-all hover:bg-slate-800/30 ${i > 0 ? 'border-t border-slate-800/50' : ''}`} data-testid={`checklist-${item.id}`}>
              {isChecked ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-slate-600 flex-shrink-0" />
              )}
              <span className={`text-sm ${isChecked ? 'text-slate-400 line-through' : 'text-slate-200'}`}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
